/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms 
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this 
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and 
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

/**
 * PaymentDebuggingPage
 * --------------------
 * The operational half of spec §6: the state-machine violation list (§6.3) and
 * the alert thresholds (§6.6).
 *
 * The violation list is a gate, not a report. While it is non-empty, either the
 * allowed-transition map is wrong or the code is doing something unintended —
 * and until that is resolved it is not safe to start REJECTING transitions.
 * That is why the enforcement control is disabled off the list's own state
 * rather than left to someone's judgement.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTheme } from '../theme/ThemeContext';
import APIHelper from '../api/APIHelper';
import envConfig from '../config/envConfig';
import { useAlertDialog } from '../components/common/AlertDialogContext';
import ProgressBarDialog from '../components/common/ProgressBar';

const apiHelper = new APIHelper({
    apiUrl: envConfig.backendApiUrl,
    tokenUrl: envConfig.backendTokenUrl,
    clientId: envConfig.clientKey,
    clientSecret: envConfig.clientSecret
});

function formatDateTime(iso) {
    if (!iso) return '—';
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? String(iso) : d.toLocaleString();
}

function Card({ title, icon, iconColor, subtitle, badge, children, theme }) {
    return (
        <div className="card border-0 shadow-sm h-100" style={{ borderRadius: theme.cardRadius }}>
            <div className="card-header bg-white border-0 pt-4 px-4 pb-0">
                <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                    <div className="d-flex align-items-center gap-2">
                        <i className={`bi ${icon}`} aria-hidden="true" style={{ fontSize: '1.2rem', color: iconColor }}></i>
                        <h6 className="fw-bold mb-0 text-dark">{title}</h6>
                    </div>
                    {badge}
                </div>
                {subtitle && <div className="text-muted mt-1" style={{ fontSize: '12px' }}>{subtitle}</div>}
            </div>
            <div className="card-body px-4 pt-3">{children}</div>
        </div>
    );
}

export default function PaymentDebuggingPage() {
    const { theme } = useTheme();
    const { id: applicationId } = useParams();
    const navigate = useNavigate();
    const { showAlertDialog } = useAlertDialog();

    const [violations, setViolations] = useState(null);
    const [config, setConfig] = useState(null);
    const [draft, setDraft] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    const reqId = useRef(0);

    const load = useCallback(async () => {
        const myId = ++reqId.current;
        setLoading(true);
        setError(null);
        try {
            const [vResp, cResp] = await Promise.all([
                apiHelper.apiCall(
                    `/arcee/payments/admin/status-transition-violations/?application_id=${applicationId}`,
                    { method: 'GET' }),
                apiHelper.apiCall(
                    `/arcee/payments/admin/alert-config/?application_id=${applicationId}`,
                    { method: 'GET' })
            ]);
            if (!vResp.ok) {
                const b = await vResp.json().catch(() => ({}));
                throw new Error(b.error || `Could not load violations (${vResp.status})`);
            }
            if (!cResp.ok) {
                const b = await cResp.json().catch(() => ({}));
                throw new Error(b.error || `Could not load alert config (${cResp.status})`);
            }
            const [v, c] = [await vResp.json(), await cResp.json()];
            if (reqId.current !== myId) return;
            setViolations(v);
            setConfig(c);
            setDraft(c);
        } catch (e) {
            if (reqId.current !== myId) return;
            setError(e.message || 'Could not load debugging data');
        } finally {
            if (reqId.current === myId) setLoading(false);
        }
    }, [applicationId]);

    useEffect(() => { load(); }, [load]);

    const save = async () => {
        setSaving(true);
        try {
            const resp = await apiHelper.apiCall(
                `/arcee/payments/admin/alert-config/?application_id=${applicationId}`,
                {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ ...draft, application_id: parseInt(applicationId, 10) })
                });
            const body = await resp.json().catch(() => ({}));
            if (!resp.ok) throw new Error(body.error || `Save failed (${resp.status})`);
            setConfig(body);
            setDraft(body);
            showAlertDialog({ title: 'Saved', message: 'Alert thresholds updated.', type: 'success' });
        } catch (e) {
            showAlertDialog({ title: 'Could not save', message: e.message, type: 'error' });
        } finally {
            setSaving(false);
        }
    };

    const dirty = config && draft && (
        Number(config.stuck_in_progress_minutes) !== Number(draft.stuck_in_progress_minutes) ||
        Number(config.webhook_failure_rate_percent) !== Number(draft.webhook_failure_rate_percent) ||
        config.notify_email !== draft.notify_email ||
        config.notify_in_app !== draft.notify_in_app
    );

    const safeToEnforce = !!violations?.safe_to_enforce;

    return (
        <div className="d-flex flex-column w-100" style={{ background: '#f8fafc', minHeight: '100vh', overflowX: 'hidden' }}>

            <div className="w-100 px-3 py-3 px-md-4 py-md-4 bg-white border-bottom d-flex flex-wrap align-items-center justify-content-between gap-3">
                <div className="d-flex align-items-center gap-3">
                    <button className="btn btn-light rounded-circle shadow-sm" aria-label="Back to application"
                        onClick={() => navigate(`/dashboard/applications/${applicationId}`)}>
                        <i className="bi bi-arrow-left" aria-hidden="true"></i>
                    </button>
                    <div>
                        <h4 className="mb-0 fw-bold text-dark">Alerts &amp; Debugging</h4>
                        <small className="text-muted d-none d-md-block">
                            State-machine violations and alert thresholds.
                        </small>
                    </div>
                </div>
                <button className="btn d-flex align-items-center gap-2"
                    onClick={() => navigate(`/dashboard/applications/${applicationId}/payment-trace`)}
                    style={{ border: `1px solid ${theme.primary}`, color: theme.primary, borderRadius: theme.buttonRadius }}>
                    <i className="bi bi-search" aria-hidden="true"></i>Payment Trace
                </button>
            </div>

            <div className="container-fluid p-3 p-md-4" style={{ maxWidth: 1400 }}>

                {loading ? (
                    <ProgressBarDialog open={true} text_message="Loading debugging data..." />
                ) : error ? (
                    <div className="card border-0 shadow-sm" style={{ borderRadius: theme.cardRadius }}>
                        <div className="card-body text-center py-5">
                            <i className="bi bi-exclamation-octagon d-block mb-3" aria-hidden="true"
                                style={{ fontSize: '2rem', color: '#dc2626' }}></i>
                            <div className="fw-semibold text-dark mb-1">This page could not be loaded</div>
                            <div className="text-muted small mb-3">{error}</div>
                            <button className="btn btn-sm" onClick={load}
                                style={{ background: theme.primary, color: '#fff', border: 'none', borderRadius: theme.buttonRadius }}>
                                <i className="bi bi-arrow-clockwise me-1" aria-hidden="true"></i>Try again
                            </button>
                        </div>
                    </div>
                ) : (
                    <>
                        <div className="card border-0 shadow-sm mb-4"
                            style={{ borderRadius: theme.cardRadius, borderLeft: `4px solid ${safeToEnforce ? '#16a34a' : '#d97706'}` }}>
                            <div className="card-body px-4 py-4 d-flex flex-wrap align-items-center gap-4">
                                <div className="flex-grow-1">
                                    <div className="d-flex align-items-center gap-2 flex-wrap">
                                        <h6 className="fw-bold mb-0">State machine — Phase 1, log-only</h6>
                                        <span className="badge" style={{ background: '#fef3c7', color: '#92400e', border: '1px solid #fde68a' }}>
                                            not enforcing
                                        </span>
                                    </div>
                                    <p className="text-muted mb-0 mt-2" style={{ fontSize: '12.5px', maxWidth: '70ch' }}>
                                        Transitions outside the allowed map are recorded and flagged, never rejected.
                                        Enforcement only becomes safe once this list has stayed empty across a real
                                        traffic window — while it is non-empty, either the map is wrong or the code is
                                        doing something unintended.
                                    </p>
                                </div>
                                <div className="text-center px-3">
                                    <div className="fw-bold" style={{ fontSize: '28px', color: safeToEnforce ? '#16a34a' : '#d97706' }}>
                                        {violations.total}
                                    </div>
                                    <div className="text-muted" style={{ fontSize: '11.5px' }}>flagged transitions</div>
                                </div>
                                <button type="button" className="btn" disabled={!safeToEnforce} title={safeToEnforce
                                    ? 'No violations recorded — enforcement can be switched on'
                                    : 'Resolve the flagged transitions before enforcing'}
                                    style={{
                                        background: safeToEnforce ? theme.primary : '#94a3b8',
                                        color: '#fff', fontWeight: 700, border: 'none',
                                        borderRadius: theme.buttonRadius,
                                        cursor: safeToEnforce ? 'pointer' : 'not-allowed'
                                    }}>
                                    Switch to enforced
                                </button>
                            </div>
                        </div>

                        <div className="row g-4">
                            <div className="col-12 col-lg-7">
                                <Card title="Flagged transitions" icon="bi-exclamation-triangle" iconColor="#d97706"
                                    subtitle="Hops recorded outside the allowed state-machine map" theme={theme}>
                                    {!violations.violations.length ? (
                                        <div className="text-center py-4">
                                            <i className="bi bi-check-circle d-block mb-2" aria-hidden="true"
                                                style={{ fontSize: '1.6rem', color: '#16a34a' }}></i>
                                            <div className="fw-semibold text-dark" style={{ fontSize: '13px' }}>
                                                No transitions outside the map
                                            </div>
                                            <div className="text-muted small mt-1">
                                                Every recorded hop is one the state machine expects.
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="table-responsive">
                                            <table className="table table-sm align-middle mb-0">
                                                <thead>
                                                    <tr style={{ fontSize: '12px', color: theme.primary }}>
                                                        <th className="fw-semibold">When</th>
                                                        <th className="fw-semibold">Transition</th>
                                                        <th className="fw-semibold">Order</th>
                                                        <th className="fw-semibold">Triggered by</th>
                                                        <th className="fw-semibold text-end"></th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {violations.violations.map(v => (
                                                        <tr key={v.id} style={{ fontSize: '12.5px' }}>
                                                            <td>{formatDateTime(v.created_at)}</td>
                                                            <td>
                                                                <code className="fw-bold" style={{ color: '#b91c1c' }}>
                                                                    {v.from_status || '—'} → {v.to_status}
                                                                </code>
                                                            </td>
                                                            <td><code>{v.gateway_order_id || '—'}</code></td>
                                                            <td>
                                                                {v.triggered_by_webhook_event_id
                                                                    ? `webhook #${v.triggered_by_webhook_event_id}`
                                                                    : v.triggered_by_admin
                                                                        ? `admin · ${v.triggered_by_admin.username}`
                                                                        : <span className="text-muted">{v.reason || '—'}</span>}
                                                            </td>
                                                            <td className="text-end">
                                                                <button type="button" className="btn btn-sm btn-light border"
                                                                    onClick={() => navigate(
                                                                        `/dashboard/applications/${applicationId}/payment-trace?q=${encodeURIComponent(v.gateway_order_id || v.payment_id)}`)}>
                                                                    Trace →
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                            {violations.total > violations.violations.length && (
                                                <div className="text-muted mt-2" style={{ fontSize: '11.5px' }}>
                                                    Showing {violations.violations.length} of {violations.total}.
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </Card>
                            </div>

                            <div className="col-12 col-lg-5">
                                <Card title="Alert thresholds" icon="bi-bell" iconColor={theme.primary}
                                    subtitle="What counts as stuck, and when a webhook failure rate is worth waking someone"
                                    theme={theme}>
                                    <div className="d-flex flex-column gap-3">
                                        <div className="d-flex align-items-center gap-3">
                                            <label htmlFor="stuck-min" className="flex-grow-1 mb-0" style={{ fontSize: '12.5px' }}>
                                                Payment stuck <code>in_progress</code> for
                                            </label>
                                            <input id="stuck-min" type="number" min="1" className="form-control form-control-sm text-end"
                                                style={{ width: 90 }} value={draft.stuck_in_progress_minutes}
                                                onChange={e => setDraft({ ...draft, stuck_in_progress_minutes: e.target.value })} />
                                            <span className="text-muted" style={{ fontSize: '12.5px', width: 34 }}>min</span>
                                        </div>
                                        <div className="d-flex align-items-center gap-3">
                                            <label htmlFor="wh-rate" className="flex-grow-1 mb-0" style={{ fontSize: '12.5px' }}>
                                                Webhook failure rate
                                            </label>
                                            <input id="wh-rate" type="number" min="0" max="100" className="form-control form-control-sm text-end"
                                                style={{ width: 90 }} value={draft.webhook_failure_rate_percent}
                                                onChange={e => setDraft({ ...draft, webhook_failure_rate_percent: e.target.value })} />
                                            <span className="text-muted" style={{ fontSize: '12.5px', width: 34 }}>%</span>
                                        </div>
                                        <div className="form-check">
                                            <input className="form-check-input" type="checkbox" id="notify-email"
                                                checked={!!draft.notify_email}
                                                onChange={e => setDraft({ ...draft, notify_email: e.target.checked })} />
                                            <label className="form-check-label" htmlFor="notify-email" style={{ fontSize: '12.5px' }}>
                                                Notify by email
                                            </label>
                                        </div>
                                        <div className="form-check">
                                            <input className="form-check-input" type="checkbox" id="notify-inapp"
                                                checked={!!draft.notify_in_app}
                                                onChange={e => setDraft({ ...draft, notify_in_app: e.target.checked })} />
                                            <label className="form-check-label" htmlFor="notify-inapp" style={{ fontSize: '12.5px' }}>
                                                Notify in-app
                                            </label>
                                        </div>
                                    </div>
                                    <div className="d-flex justify-content-end align-items-center gap-2 mt-4">
                                        {dirty && <span className="small" style={{ color: '#b45309' }}>Unsaved changes</span>}
                                        <button type="button" className="btn btn-sm" onClick={save} disabled={saving || !dirty}
                                            style={{
                                                background: dirty ? theme.primary : '#94a3b8', color: '#fff',
                                                border: 'none', borderRadius: theme.buttonRadius
                                            }}>
                                            {saving ? 'Saving…' : 'Save thresholds'}
                                        </button>
                                    </div>
                                </Card>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
